import type { AppConfig } from '@/config';
import { authority } from '@/features/auth/oidcSettings';

/**
 * A known Keycloak account in this browser. Under the WP-04 fallback
 * (`login_hint`, see PLAN-FASE1.md "Meerdere accounts › Uitkomst spike") this
 * store never holds tokens — `oidc-client-ts` keeps those in `sessionStorage`
 * for whichever single account currently has a live session. Switching to a
 * different known account re-authenticates it with `login_hint`, which skips
 * the password screen as long as Keycloak's SSO session is still valid.
 */
export interface Account {
  sub: string;
  name?: string;
  email?: string;
  /** ISO timestamp of when this account was first added to the store. */
  addedAt: string;
}

export interface AccountStoreData {
  accounts: Account[];
  activeSub: string | null;
}

const EMPTY_STORE: AccountStoreData = { accounts: [], activeSub: null };

/** Scoped like `userStorageKey` so different environments never collide. */
export function accountsStorageKey(config: AppConfig): string {
  return `nolatr.accounts:${authority(config)}:${config.keycloakClientId}`;
}

// A tiny cache + listener set so `useSyncExternalStore` (in useSession.ts)
// gets a stable snapshot reference between reads without re-parsing on every
// render, and so writes in this tab notify subscribers synchronously — a
// native `storage` event only fires in *other* tabs, never the one that
// wrote the value. Keyed off the raw string (not just the storage key) so a
// write from outside these functions — another tab, a test's
// `localStorage.clear()` — is never served as a stale snapshot.
let cache: { key: string; raw: string | null; store: AccountStoreData } | null = null;
const listeners = new Set<() => void>();

function parseStore(raw: string | null): AccountStoreData {
  if (!raw) return EMPTY_STORE;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      !Array.isArray((parsed as AccountStoreData).accounts)
    ) {
      return EMPTY_STORE;
    }
    const data = parsed as AccountStoreData;
    return { accounts: data.accounts, activeSub: data.activeSub ?? null };
  } catch {
    return EMPTY_STORE;
  }
}

function writeStore(config: AppConfig, store: AccountStoreData): AccountStoreData {
  const key = accountsStorageKey(config);
  const raw = JSON.stringify(store);
  localStorage.setItem(key, raw);
  cache = { key, raw, store };
  listeners.forEach((listener) => listener());
  return store;
}

/** Current store contents, read fresh whenever the underlying value changed and cached otherwise. */
export function getAccountStore(config: AppConfig): AccountStoreData {
  const key = accountsStorageKey(config);
  const raw = localStorage.getItem(key);
  if (cache && cache.key === key && cache.raw === raw) return cache.store;
  const store = parseStore(raw);
  cache = { key, raw, store };
  return store;
}

/**
 * Subscribes to store changes: writes in this tab (via the functions above)
 * and `storage` events from other tabs signing an account in or out.
 */
export function subscribeAccountStore(listener: () => void): () => void {
  listeners.add(listener);
  const onStorage = (event: StorageEvent) => {
    if (event.storageArea !== localStorage) return;
    listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

/** Adds a newly signed-in account, or refreshes a known one's profile fields, and marks it active. */
export function upsertAccount(
  config: AppConfig,
  account: Pick<Account, 'sub' | 'name' | 'email'>,
): AccountStoreData {
  const store = getAccountStore(config);
  const existing = store.accounts.find((a) => a.sub === account.sub);
  const others = store.accounts.filter((a) => a.sub !== account.sub);
  const next: AccountStoreData = {
    accounts: [...others, { ...account, addedAt: existing?.addedAt ?? new Date().toISOString() }],
    activeSub: account.sub,
  };
  return writeStore(config, next);
}

/** Marks a known account as active without changing the list. No-op if it isn't in the store. */
export function setActiveAccount(config: AppConfig, sub: string): AccountStoreData {
  const store = getAccountStore(config);
  if (!store.accounts.some((a) => a.sub === sub)) return store;
  return writeStore(config, { ...store, activeSub: sub });
}

/**
 * Drops an account from the store. If it was the active one, the next
 * account (if any) becomes active — the caller is responsible for actually
 * switching the live session to match.
 */
export function removeAccount(config: AppConfig, sub: string): AccountStoreData {
  const store = getAccountStore(config);
  const accounts = store.accounts.filter((a) => a.sub !== sub);
  const activeSub = store.activeSub === sub ? (accounts[0]?.sub ?? null) : store.activeSub;
  return writeStore(config, { accounts, activeSub });
}

/** Clears every known account — used by "sign out everywhere". */
export function clearAccountStore(config: AppConfig): AccountStoreData {
  return writeStore(config, EMPTY_STORE);
}
